import { useState } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Textarea } from "@/components/ui/textarea";
import { CheckCircle2, ChevronLeft, Loader2, Users } from "lucide-react";
import { API } from "@/constants";
import Disclaimer from "@/components/shared/Disclaimer";
import Footer from "@/components/shared/Footer";

const DepositionOutlinePage = () => {
  const [form, setForm] = useState({
    case_type: 'personal_injury',
    jurisdiction: 'us_federal',
    deponent_name: '',
    deponent_role: 'witness',
    case_summary: '',
    key_topics: [],
    goals: [],
    time_limit_hours: 7
  });
  const [newTopic, setNewTopic] = useState('');
  const [result, setResult] = useState(null);
  const [loading, setLoading] = useState(false);
  
  const addTopic = () => { if (newTopic.trim()) { setForm(f => ({...f, key_topics: [...f.key_topics, newTopic.trim()]})); setNewTopic(''); }};
  
  const generate = async () => {
    setLoading(true);
    try {
      const res = await axios.post(`${API}/practice/deposition-outline`, form);
      setResult(res.data);
    } catch (e) {
      console.error(e);
    } finally {
      setLoading(false);
    }
  };
  
  return (
    <main className="min-h-screen bg-slate-50" data-testid="deposition-page">
      <Disclaimer />
      <div className="container-shell py-8 max-w-5xl">
        <div className="mb-6">
          <Link to="/tools" className="text-sm text-muted-foreground hover:text-foreground flex items-center gap-1 mb-4">
            <ChevronLeft className="h-4 w-4" /> Back to Tools
          </Link>
          <h1 className="text-3xl font-bold" style={{ fontFamily: "'Fraunces', serif" }}>Deposition Outlines</h1>
          <p className="text-muted-foreground">Generate comprehensive deposition question outlines</p>
        </div>
        
        {!result ? (
          <div className="grid gap-6 lg:grid-cols-3">
            <div className="lg:col-span-2 space-y-6">
              <Card>
                <CardHeader><CardTitle>Deposition Details</CardTitle></CardHeader>
                <CardContent className="space-y-4">
                  <div className="grid gap-4 md:grid-cols-2">
                    <div className="space-y-1.5">
                      <Label>Deponent Name</Label>
                      <Input placeholder="John Doe" value={form.deponent_name} onChange={e => setForm(f => ({...f, deponent_name: e.target.value}))} />
                    </div>
                    <div className="space-y-1.5">
                      <Label>Deponent Role</Label>
                      <Select value={form.deponent_role} onValueChange={v => setForm(f => ({...f, deponent_role: v}))}>
                        <SelectTrigger><SelectValue /></SelectTrigger>
                        <SelectContent>
                          <SelectItem value="party">Party</SelectItem>
                          <SelectItem value="witness">Fact Witness</SelectItem>
                          <SelectItem value="expert">Expert Witness</SelectItem>
                          <SelectItem value="corporate">Corporate Rep (30(b)(6))</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                  </div>
                  <div className="space-y-1.5">
                    <Label>Case Summary</Label>
                    <Textarea placeholder="Brief summary of the case..." value={form.case_summary} onChange={e => setForm(f => ({...f, case_summary: e.target.value}))} rows={3} />
                  </div>
                  <div className="space-y-1.5">
                    <Label>Topics to Cover</Label>
                    <div className="flex gap-2">
                      <Input placeholder="Add topic..." value={newTopic} onChange={e => setNewTopic(e.target.value)} onKeyDown={e => e.key === 'Enter' && addTopic()} />
                      <Button onClick={addTopic} variant="outline">Add</Button>
                    </div>
                    <div className="flex flex-wrap gap-1 mt-2">
                      {form.key_topics.map((t, i) => <Badge key={i} variant="secondary">{t} <button onClick={() => setForm(f => ({...f, key_topics: f.key_topics.filter((_, idx) => idx !== i)}))} className="ml-1">×</button></Badge>)}
                    </div>
                  </div>
                </CardContent>
              </Card>
            </div>
            
            <Card className="bg-violet-600 text-white border-0">
              <CardContent className="p-6">
                <Button className="w-full h-14 text-lg gap-2 bg-white text-violet-700 hover:bg-white/90" onClick={generate} disabled={loading || !form.deponent_name}>
                  {loading ? <><Loader2 className="h-5 w-5 animate-spin" /> Generating...</> : <><Users className="h-5 w-5" /> Generate Outline</>}
                </Button>
              </CardContent>
            </Card>
          </div>
        ) : (
          <div className="space-y-6 animate-fade-in">
            <Card className="bg-violet-600 text-white border-0">
              <CardContent className="p-6">
                <h2 className="text-2xl font-bold">Deposition Outline</h2>
                <p className="text-white/80">{result.deponent_name} ({result.deponent_role})</p>
                <Button variant="outline" onClick={() => setResult(null)} className="mt-4 text-white border-white/20">New Outline</Button>
              </CardContent>
            </Card>
            
            <Card>
              <CardContent className="p-6">
                <div className="prose prose-sm max-w-none whitespace-pre-wrap">{result.content}</div>
              </CardContent>
            </Card>
            
            {result.deposition_tips && (
              <Card>
                <CardHeader><CardTitle>Deposition Tips</CardTitle></CardHeader>
                <CardContent>
                  <ul className="space-y-1">{result.deposition_tips.map((tip, i) => <li key={i} className="text-sm flex gap-2"><CheckCircle2 className="h-4 w-4 text-emerald-500 mt-0.5" />{tip}</li>)}</ul>
                </CardContent>
              </Card>
            )}
          </div>
        )}
      </div>
      <Footer />
    </main>
  );
};


export default DepositionOutlinePage;
